/*
 * Osmo Supply — Basic GSAP Slider (Watch CSS).
 * Used verbatim per resource guidance. Requires gsap, Draggable, InertiaPlugin
 * (gsap loads globally in theme.liquid; Draggable + InertiaPlugin load from the section).
 */

gsap.registerPlugin(Draggable, InertiaPlugin);

function initBasicGSAPSlider() {
  document.querySelectorAll('[data-gsap-slider-init]').forEach(root => {
    if (root._sliderDraggable) root._sliderDraggable.kill();

    const collection = root.querySelector('[data-gsap-slider-collection]');
    const track      = root.querySelector('[data-gsap-slider-list]');

    // [ARB] Tear down clone slides from a previous init
    track.querySelectorAll('[data-gsap-slider-clone]').forEach(clone => clone.remove());

    const originalItems = Array.from(root.querySelectorAll('[data-gsap-slider-item]'));
    const controls   = Array.from(root.querySelectorAll('[data-gsap-slider-control]'));
    const loopEnabled = root.getAttribute('data-gsap-slider-loop') === 'true';
    const centerEnabled = root.getAttribute('data-gsap-slider-center') === 'true';

    /* arbt: every accessible name and roledescription this engine injects is a
       translated string, rendered onto the root as a data-* attribute by
       sections/arb-image-carousel.liquid (and mirrored onto the root that
       assets/arb-article.js builds for blog-article galleries) so Translate & Adapt
       can reach it. The English literals stay ONLY as `||` fallbacks: a stale cached
       section, or a slider root from somewhere that has not been given the set, would
       otherwise leave the region, the list and every slide unnamed. */
    const carouselLabels = {
      region: root.getAttribute('data-carousel-label') || '',
      regionRoledescription: root.getAttribute('data-carousel-roledescription') || 'carousel',
      slider: root.getAttribute('data-slider-label') || 'Slider',
      listRoledescription: root.getAttribute('data-list-roledescription') || 'Slides List',
      list: root.getAttribute('data-list-label') || 'Slides',
      slide: root.getAttribute('data-slide-label') || 'Slide __INDEX__ of __COUNT__',
      slideRoledescription: root.getAttribute('data-slide-roledescription') || 'Slide',
      prev: root.getAttribute('data-prev-label') || 'Previous Slide',
      next: root.getAttribute('data-next-label') || 'Next Slide'
    };

    // Inject aria attributes
    const sliderLabel = root.getAttribute('aria-label') || carouselLabels.region || carouselLabels.slider;
    root.setAttribute('role','region');
    root.setAttribute('aria-roledescription',carouselLabels.regionRoledescription);
    root.setAttribute('aria-label', sliderLabel);
    collection.setAttribute('role','group');
    collection.setAttribute('aria-roledescription',carouselLabels.listRoledescription);
    collection.setAttribute('aria-label',carouselLabels.list);
    const slideLabelTemplate = carouselLabels.slide;
    const slideRoledescription = carouselLabels.slideRoledescription;
    originalItems.forEach((slide,i) => {
      slide.setAttribute('role','group');
      slide.setAttribute('aria-roledescription',slideRoledescription);
      slide.setAttribute('aria-label',
        slideLabelTemplate
          .replace('__INDEX__', i+1)
          .replace('__COUNT__', originalItems.length));
      slide.setAttribute('aria-hidden','true');
      /* arbt: modified — `aria-selected` was set here and in the update loop below on an
         element whose role is "group". aria-selected is only allowed on the roles that
         have a selection model (option, tab, row, gridcell, treeitem); on a group it is
         a disallowed attribute, which axe reported as aria-allowed-attr (CRITICAL) on
         /pages/our-story. Only the two or three slides that are NOT aria-hidden got
         flagged, which is why a nine-slide carousel showed up as two nodes.
         The ARIA APG carousel pattern conveys the active slide with
         role="group" + aria-roledescription="slide" and the group's own label, exactly
         as this does — the selection attribute was never carrying anything.
         `data-gsap-slider-item-status` remains, and it is what the CSS keys on. */
      slide.setAttribute('tabindex','-1');
    });
    controls.forEach(btn => {
      const dir = btn.getAttribute('data-gsap-slider-control');
      btn.setAttribute('role','button');
      /* arbt: this line is why the section's Previous/Next label SETTINGS were dead —
         it overwrites whatever the Liquid rendered into the .sr-only span. Both now
         come from the same locale key the span does, so the two cannot disagree. */
      btn.setAttribute('aria-label', dir==='prev' ? carouselLabels.prev : carouselLabels.next);
      btn.disabled = true;
      btn.setAttribute('aria-disabled','true');
    });

    // Determine if slider runs
    const styles      = getComputedStyle(root);
    const statusVar   = styles.getPropertyValue('--slider-status').trim();
    let   spvVar      = parseFloat(styles.getPropertyValue('--slider-spv'));
    const rect        = originalItems[0].getBoundingClientRect();
    const marginRight = parseFloat(getComputedStyle(originalItems[0]).marginRight);
    const slideWidth  = rect.width;
    const slideW      = slideWidth + marginRight;
    const centerOffset = centerEnabled ? (collection.clientWidth - slideWidth) / 2 : 0;
    if (isNaN(spvVar)) {
      spvVar = collection.clientWidth / slideW;
    }
    const slideCount    = originalItems.length;
    const spv           = Math.max(1, Math.min(spvVar, slideCount));
    const sliderEnabled = statusVar==='on' && spv < slideCount;
    root.setAttribute('data-gsap-slider-status', sliderEnabled ? 'active' : 'not-active');

    if (!sliderEnabled) {
      // Teardown when disabled
      track.removeAttribute('style');
      track.onmouseenter = null;
      track.onmouseleave = null;
      track.removeAttribute('data-gsap-slider-list-status');
      root.removeAttribute('role');
      root.removeAttribute('aria-roledescription');
      root.removeAttribute('aria-label');
      collection.removeAttribute('role');
      collection.removeAttribute('aria-roledescription');
      collection.removeAttribute('aria-label');
      originalItems.forEach(slide => {
        slide.removeAttribute('role');
        slide.removeAttribute('aria-roledescription');
        slide.removeAttribute('aria-label');
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('tabindex');
        slide.removeAttribute('data-gsap-slider-item-status');
      });
      controls.forEach(btn => {
        btn.disabled = false;
        btn.removeAttribute('role');
        btn.removeAttribute('aria-label');
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('data-gsap-slider-control-status');
      });
      return;
    }

    // [ARB] Infinite loop: bookend the track with cloned slides.
    // The "before" set must be in FORWARD order so the track reads as three
    // identical sequences ([A,B,…,I][A,B,…,I][A,B,…,I]) and the seam is
    // seamless — i.e. slide 0's left neighbour is the last slide, not a clone
    // of slide 0. Inserting in reverse put a duplicate of the centred slide in
    // the left peek. This only changes which slide peeks on the left; the snap
    // points (which index off the originals at [slideCount .. 2*slideCount-1])
    // are unaffected.
    if (loopEnabled) {
      for (let i = 0; i < originalItems.length; i++) {
        const cloneBefore = originalItems[i].cloneNode(true);
        cloneBefore.setAttribute('data-gsap-slider-clone', 'before');
        cloneBefore.setAttribute('aria-hidden', 'true');
        cloneBefore.removeAttribute('data-gsap-slider-item');
        track.insertBefore(cloneBefore, originalItems[0]);
      }
      originalItems.forEach(item => {
        const cloneAfter = item.cloneNode(true);
        cloneAfter.setAttribute('data-gsap-slider-clone', 'after');
        cloneAfter.setAttribute('aria-hidden', 'true');
        cloneAfter.removeAttribute('data-gsap-slider-item');
        track.appendChild(cloneAfter);
      });
    }

    const allSlides = Array.from(track.querySelectorAll('.gsap-slider__item'));

    // [ARB] Belt-and-braces: block native HTML5 drag (the image "ghost" that
    // lifts on grab) and any text selection so pointer drags reach Draggable.
    track.addEventListener('dragstart', e => e.preventDefault());
    track.addEventListener('selectstart', e => e.preventDefault());
    track.addEventListener('pointerdown', () => {
      const sel = window.getSelection && window.getSelection();
      if (sel && sel.rangeCount) sel.removeAllRanges();
    });

    // Track hover state
    track.onmouseenter = () => {
      track.setAttribute('data-gsap-slider-list-status','grab');
    };
    track.onmouseleave = () => {
      track.removeAttribute('data-gsap-slider-list-status');
    };

    //Ccalculate bounds and snap points
    const vw        = collection.clientWidth;
    const tw        = track.scrollWidth;
    const maxScroll = Math.max(tw - vw, 0);
    const minX      = loopEnabled ? -(slideCount * 2) * slideW + centerOffset : -maxScroll + centerOffset;
    const maxX      = centerOffset;
    const maxIndex  = maxScroll / slideW;
    const full      = Math.floor(maxIndex);
    const snapPoints = [];
    if (loopEnabled) {
      for (let i = 0; i < slideCount; i++) {
        snapPoints.push(-(slideCount + i) * slideW + centerOffset);
      }
    } else {
      for (let i = 0; i <= full; i++) {
        snapPoints.push(-i * slideW + centerOffset);
      }
      if (full < maxIndex) {
        snapPoints.push(-maxIndex * slideW + centerOffset);
      }
    }

    const loopSetWidth = slideCount * slideW;
    // [ARB] wrap window edges sit half a slide beyond the end snap points —
    // never ON a snap/tween endpoint. Seam tweens end exactly one set width
    // past an end snap, so a boundary AT that point made the wrap decision a
    // float-rounding coin toss (the "stuck on Slide 8" seam bug).
    const loopMinX     = -(slideCount * 2 - 0.5) * slideW + centerOffset;
    const loopMaxX     = -(slideCount - 0.5) * slideW + centerOffset;

    function wrapX(x) {
      if (!loopEnabled) return x;
      while (x > loopMaxX) x -= loopSetWidth;
      while (x <= loopMinX) x += loopSetWidth;
      return x;
    }

    let activeIndex    = 0;
    const setX         = gsap.quickSetter(track,'x','px');
    let collectionRect = collection.getBoundingClientRect();

    function syncPosition(x, draggable) {
      setX(x);
      if (draggable) {
        draggable.x = x;
        draggable.update();
      }
    }

    function updateStatus(x) {
      if (!loopEnabled && (x > maxX || x < minX)) {
        return;
      }

      // Clamp and find closest snap (use wrapped position for loop index only)
      const calcX = loopEnabled
        ? wrapX(x)
        : (x > maxX ? maxX : (x < minX ? minX : x));
      let closest = snapPoints[0];
      snapPoints.forEach(pt => {
        if (Math.abs(pt - calcX) < Math.abs(closest - calcX)) {
          closest = pt;
        }
      });
      activeIndex = snapPoints.indexOf(closest);

      // Update Slide Attributes
      allSlides.forEach(slide => {
        const isClone = slide.hasAttribute('data-gsap-slider-clone');
        const r           = slide.getBoundingClientRect();
        const leftEdge    = r.left - collectionRect.left;
        const slideCenter = leftEdge + r.width/2;
        const inView      = slideCenter > 0 && slideCenter < collectionRect.width;

        if (isClone) {
          slide.setAttribute('data-gsap-slider-item-status', inView ? 'inview' : 'not-active');
          return;
        }

        const i = originalItems.indexOf(slide);
        const status      = i === activeIndex ? 'active' : inView ? 'inview' : 'not-active';

        slide.setAttribute('data-gsap-slider-item-status', status);
        /* arbt: no aria-selected — see the note at init. role="group" does not take it. */
        slide.setAttribute('aria-hidden',      inView ? 'false' : 'true');
        slide.setAttribute('tabindex',         i === activeIndex ? '0'    : '-1');
      });

      // Update Controls
      controls.forEach(btn => {
        const dir = btn.getAttribute('data-gsap-slider-control');
        const can = loopEnabled
          ? true
          : dir === 'prev'
            ? activeIndex > 0
            : activeIndex < snapPoints.length - 1;

        btn.disabled = !can;
        btn.setAttribute('aria-disabled', can ? 'false' : 'true');
        btn.setAttribute('data-gsap-slider-control-status', can ? 'active' : 'not-active');
      });
    }

    controls.forEach(btn => {
      const dir = btn.getAttribute('data-gsap-slider-control');
      // [ARB] init runs more than once per page (arb-article.js hands off here
      // AND this file's own DOMContentLoaded/resize hooks re-init). A stale
      // listener's activeIndex stays 0 forever, so its prev branch teleports
      // the track a full loop-set left on every click — replace, never stack.
      if (btn._sliderClickHandler) btn.removeEventListener('click', btn._sliderClickHandler);
      btn._sliderClickHandler = () => {
        if (btn.disabled) return;
        if (loopEnabled) {
          const draggable = root._sliderDraggable;
          const currentX  = gsap.getProperty(track, 'x');
          const tweenVars = {
            duration: 0.4,
            onUpdate: () => updateStatus(gsap.getProperty(track, 'x')),
            onComplete: () => {
              const wrapped = wrapX(gsap.getProperty(track, 'x'));
              syncPosition(wrapped, draggable);
              updateStatus(wrapped);
            }
          };

          gsap.killTweensOf(track);

          // Prev at first: teleport to identical clone, then animate one step into last slide
          if (dir === 'prev' && activeIndex === 0) {
            const startX = currentX - loopSetWidth;
            syncPosition(startX, draggable);
            gsap.to(track, { ...tweenVars, x: startX + slideW });
            return;
          }

          // Next at last: animate into clone zone, then snap back on complete
          const step = dir === 'next' ? -slideW : slideW;
          gsap.to(track, { ...tweenVars, x: currentX + step });
          return;
        }
        const delta = dir === 'next' ? 1 : -1;
        const target = activeIndex + delta;
        if (target < 0 || target >= snapPoints.length) return;
        gsap.to(track, {
          duration: 0.4,
          x: snapPoints[target],
          onUpdate: () => updateStatus(gsap.getProperty(track,'x'))
        });
      };
      btn.addEventListener('click', btn._sliderClickHandler);
    });

    // Initialize Draggable
    root._sliderDraggable = Draggable.create(track, {
      type: 'x',
      // cursor: 'inherit',
      // activeCursor: 'inherit',
      inertia: true,
      bounds: loopEnabled ? { minX: minX, maxX: maxX } : {minX, maxX},
      throwResistance: 2000,
      dragResistance: 0.05,
      maxDuration: 0.6,
      minDuration: 0.2,
      edgeResistance: loopEnabled ? 0 : 0.75,
      snap: loopEnabled
        ? {
            x: value => {
              const wrapped = wrapX(value);
              let closest = snapPoints[0];
              snapPoints.forEach(pt => {
                if (Math.abs(pt - wrapped) < Math.abs(closest - wrapped)) {
                  closest = pt;
                }
              });
              // Snap to the replica nearest the raw position to avoid flying across the track
              let best = closest;
              let bestDist = Math.abs(value - closest);
              [closest, closest - loopSetWidth, closest + loopSetWidth].forEach(candidate => {
                const dist = Math.abs(value - candidate);
                if (dist < bestDist) {
                  best = candidate;
                  bestDist = dist;
                }
              });
              return best;
            },
            duration: 0.4
          }
        : {x: snapPoints, duration: 0.4},
      onPress() {
        track.setAttribute('data-gsap-slider-list-status','grabbing');
        collectionRect = collection.getBoundingClientRect();
      },
      onDrag() {
        if (loopEnabled) {
          const wrapped = wrapX(this.x);
          if (wrapped !== this.x) syncPosition(wrapped, this);
        }
        updateStatus(this.x);
      },
      onThrowUpdate() {
        if (loopEnabled) {
          const wrapped = wrapX(this.x);
          if (wrapped !== this.x) syncPosition(wrapped, this);
        }
        updateStatus(this.x);
      },
      onThrowComplete() {
        const finalX = loopEnabled ? wrapX(this.endX) : this.endX;
        syncPosition(finalX, this);
        updateStatus(finalX);
        track.setAttribute('data-gsap-slider-list-status','grab');
      },
      onRelease() {
        if (loopEnabled) {
          const wrapped = wrapX(this.x);
          if (wrapped !== this.x) syncPosition(wrapped, this);
        }
        updateStatus(this.x);
        track.setAttribute('data-gsap-slider-list-status','grab');
      }
    })[0];

    // Initial state
    setX(loopEnabled ? snapPoints[0] : centerOffset);
    updateStatus(loopEnabled ? snapPoints[0] : centerOffset);
  });
}

// Debouncer: For resizing the window
function debounceOnWidthChange(fn, ms) {
  let last = innerWidth, timer;
  return function(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (innerWidth !== last) {
        last = innerWidth;
        fn.apply(this, args);
      }
    }, ms);
  };
}

window.addEventListener('resize', debounceOnWidthChange(initBasicGSAPSlider, 200));

// Initialize Basic GSAP Slider
document.addEventListener('DOMContentLoaded', function() {
  initBasicGSAPSlider();
});

/* [ARB] theme editor re-renders sections */
document.addEventListener('shopify:section:load', function() {
  initBasicGSAPSlider();
});
